import { useState } from 'react';
import { api } from '../../api/client';
import { color, fontSize, radius } from '../../theme/tokens';
import { toMarkdown, toTsv } from './clipboard';
import type { ColumnSpec } from './format';

/**
 * Copy and export (spec 006 T071, FR-A06/FR-A67).
 *
 * ## ⚠ The copy carries FULL figures, never the compacted faces
 *
 * `20.14K` pasted into a spreadsheet is text, not a number — and a reader who copies a table is
 * almost always about to do arithmetic on it. `clipboard.ts` does that; this is the control.
 *
 * ## ⚠ The export is NOT "what is on screen"
 *
 * The store holds up to `AI_EXPORT_MAX_ROWS`; the browser was sent `AI_MAX_ROWS`. The face says so
 * **before** the reader clicks, and the button is addressed by `resultId` so the server exports the
 * result the reader was given — never a re-run, which can return different rows.
 */

export interface CopyControlProps {
  columns: readonly ColumnSpec[];
  rows: readonly unknown[][];
  totalRows: number;
  prose?: string;
  /** `null` when the turn produced no stored result — then there is nothing to export. */
  resultId: string | null;
}

type Status = 'idle' | 'copied-md' | 'copied-tsv' | 'exporting' | 'exported' | 'failed';

export function CopyControl({ columns, rows, totalRows, prose, resultId }: CopyControlProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [sheetUrl, setSheetUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function copy(kind: 'md' | 'tsv') {
    const payload = { columns, rows, totalRows };
    const text = kind === 'md' ? toMarkdown(payload, prose) : toTsv(payload);
    try {
      await navigator.clipboard.writeText(text);
      setStatus(kind === 'md' ? 'copied-md' : 'copied-tsv');
      window.setTimeout(() => setStatus('idle'), 2000);
    } catch {
      setStatus('failed');
      setMessage('Could not copy to the clipboard.');
    }
  }

  async function exportToSheets() {
    if (!resultId) return;
    setStatus('exporting');
    setProgress(null);
    setMessage(null);
    setSheetUrl(null);

    try {
      await api.analystExport({ resultId, returnTo: window.location.href }, (event) => {
        const e = event as unknown as {
          t?: string;
          type?: string;
          percent?: number;
          url?: string;
          message?: string;
          consentUrl?: string;
        };
        const kind = e.t ?? e.type;

        /*
         * ⚠ Consent is a FULL PAGE NAVIGATION, so the export has to be resumable from the
         * address alone — React state does not survive it. `returnTo` above carries the way back.
         */
        if (kind === 'consent_required' && e.consentUrl) {
          window.location.href = e.consentUrl;
          return;
        }
        // ⚠ The percentage is rows Google has ACCEPTED, never an elapsed-time guess: a ring
        // animated against a guessed duration looks identical and invites planning around a
        // number the page invented.
        if (kind === 'progress' && typeof e.percent === 'number') setProgress(e.percent);
        if (kind === 'done' || kind === 'complete') {
          setStatus('exported');
          setSheetUrl(e.url ?? null);
        }
        if (kind === 'failed') {
          setStatus('failed');
          setMessage(e.message ?? 'The export did not complete.');
        }
      });
    } catch {
      setStatus('failed');
      setMessage('The export did not complete.');
    }
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
      <Action onClick={() => void copy('md')} label={status === 'copied-md' ? 'Copied' : 'Copy'} />
      <Action onClick={() => void copy('tsv')} label={status === 'copied-tsv' ? 'Copied' : 'Copy as TSV'} />

      {resultId ? (
        <Action
          onClick={() => void exportToSheets()}
          label={
            status === 'exporting'
              ? `Exporting${progress === null ? '…' : ` ${Math.max(1, Math.round(progress))}%`}`
              : 'Export to Sheets'
          }
        />
      ) : null}

      {/*
        ⚠ A real anchor, never `window.open`: a popup ~74 seconds after the gesture that caused it
        is exactly what a blocker stops, and the sheet would silently not open.
      */}
      {sheetUrl ? (
        <a
          href={sheetUrl}
          target="_blank"
          rel="noreferrer"
          // The brand teal, the same token the sidebar mark and the sign-in button use — there
          // is no separate link token in this palette, and a literal hex is a lint error.
          style={{ fontSize: fontSize.caption, color: color.brandFrom }}
        >
          Open sheet
        </a>
      ) : null}

      {message ? (
        // ⚠ A failure has no other surface, so it is visible rather than sr-only.
        <span role="status" style={{ fontSize: fontSize.caption, color: color.inkMuted }}>
          {message}
        </span>
      ) : null}
    </div>
  );
}

function Action({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'transparent',
        border: `1px solid ${color.border}`,
        borderRadius: radius.md,
        padding: '2px 8px',
        fontSize: fontSize.caption,
        color: color.inkMuted,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}
